import React, { useState, useEffect, useRef } from "react";
import { StyleSheet, View, SafeAreaView, Platform, useWindowDimensions, SectionList, ActivityIndicator, } from "react-native";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useAccountContext } from "../../../hooks/useAccountContext";
import CustomInput from "../../../components/CustomInput";
import CustomControlledCalendar from "../../../components/ControlledComponents/CustomControlledCalendar";
import CustomControlledInput from "../../../components/ControlledComponents/CustomControlledInput";
import CustomControlledDropdown from "../../../components/ControlledComponents/CustomControlledDropdown";
import { formatDateForUI, changeDateFromDDMMYYYYtoYYYYMMDD } from "../../../constants/FormatDate";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { v4 as uuidv4 } from 'uuid';
import { useHRAdminEmplAlerts } from "../../../hooks/useHRAdminEmplAlerts";
import { showMessage, hideMessage } from "react-native-flash-message";
import { useForm, useWatch, useFieldArray } from "react-hook-form";
import { CONTROL_FORM_FIELDS } from "../../../constants/ControlFormFields";

const HRAdminEmpAlerts = (props) => {
    //#region Definitions
    const { width } = useWindowDimensions();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width);
    const { account } = useAccountContext();
    const { t } = useTranslation();
    const [sections, setSections] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [editable, setEditable] = useState([]);
    const originalEditableRef = useRef(null);
    const { GetAlerts, GetNonEditableMonitors, GetEditableMonitors, SaveEmplAlerts, isLoading } = useHRAdminEmplAlerts();
    const { COMMENTS } = CONTROL_FORM_FIELDS.EMPLALERTS;
    const MS_PER_DAY = 86_400_000;
    const {
        control,
        handleSubmit,
        formState: { dirtyFields },
        resetField,
        setValue,
        getValues
    } = useForm({
        defaultValues: { editable: editable },
        mode: "onBlur",
        reValidateMode: "onChange",
    });
    const { fields, append, remove, replace } = useFieldArray({ control, name: 'editable' });
    //#endregion

    //#region UseEffect
    useEffect(() => {
        GetAlerts(props.selectedEmployee.employeeID).then((alerts) => {
            setAlerts(alerts);
            GetNonEditableMonitors(props.selectedEmployee.employeeID).then((nonEditable) => {
                GetEditableMonitors(props.selectedEmployee.employeeID).then((editable) => {
                    // * Keep original data to compare with dirty date on submit to sent only changes INSERT/UPDATE/DELETE
                    const shaped = editable.map((r) => ({
                        ...r,
                        alertDate: r.alertDate ? { startDate: changeDateFromDDMMYYYYtoYYYYMMDD(formatDateForUI(r.alertDate)), endDate: null } : null,
                    }));
                    originalEditableRef.current = shaped;
                    setEditable(shaped);
                    replace(shaped)
                    const tempSections = [
                        {
                            key: "standard",
                            title: t('common:standardAlerts'),
                            editable: false,
                            data: nonEditable,
                        },
                        {
                            key: "custom",
                            title: t('common:customAlerts'),
                            editable: true,
                            data: shaped,
                        },
                    ];
                    setSections(tempSections);
                }).catch((ex) => { console.error(ex) })
            }).catch((ex) => { console.error(ex) })
        }).catch((ex) => { console.error(ex) })
    }, [])
    //#endregion

    const onSubmit = (data) => {
        const current = data.editable;
        const original = originalEditableRef.current;
        const originalByGuid = new Map(original.map((r) => [r.rowguid, r]));
        const currentGuids = new Set(current.map((r) => r.rowguid));

        const toPayloadRow = (r) => ({
            ...r,
            alertDate: r.alertDate?.startDate ?? null,
        });

        const inserts = current
            .filter((r) => !originalByGuid.has(r.rowguid))
            .map(toPayloadRow);

        const updates = current
            .filter((r) => {
                const orig = originalByGuid.get(r.rowguid);
                return orig && JSON.stringify(orig) !== JSON.stringify(r);
            })
            .map(toPayloadRow);

        const deletes = original.filter((r) => !currentGuids.has(r.rowguid));

        const payload = {
            userId: account.employeeId,
            inserts,
            updates,
            deletes: deletes.map((r) => r.rowguid),
        };

        if (inserts.length === 0 && updates.length === 0 && deletes.length === 0) {
            showMessage({ message: t("common:noChanges"), type: "info" });
            return;
        }

        showMessage({
            message: t("common:saving"),
            type: "info",
            autoHide: false,
        });

        SaveEmplAlerts(payload).then(() => {
            showMessage({
                message: t("common:saveSuccess"),
                type: "success",
                duration: 3000,
            });
        }).catch((ex) => {
            showMessage({
                message: t("common:saveError"),
                type: "error",
                duration: 4000,
            });
        })
    }

    //#region Helpers 
    const daysUntilCalculation = (ddmmyyyy) => {
        if (!ddmmyyyy) return null;

        const [dd, mm, yyyy] = ddmmyyyy.split("/").map(Number);
        if (!dd || !mm || !yyyy) return null;

        const target = new Date(yyyy, mm - 1, dd);

        if (
            target.getDate() !== dd ||
            target.getMonth() !== mm - 1 ||
            target.getFullYear() !== yyyy
        ) {
            return null;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return Math.round((target - today) / MS_PER_DAY);
    }

    const makeEmptyRow = (employeeid) => ({
        employeeid,
        monitorui: "",
        comments: "",
        rowguid: uuidv4(),
        alertDate: null
    });


    const handleAddRow = (employeeid) => {
        const rows = getValues("editable") ?? [];
        const hasEmptyMonitor = rows.some((r) => !r?.monitorui);
        if (hasEmptyMonitor) {
            showMessage({
                message: t("common:completeExistingAlerts"),
                description: t("common:selectMonitorFirst"),
                type: "warning",
            });
            return;
        }

        append(makeEmptyRow(employeeid));
    };
    const displaySections = [
        sections.find(s => s.key === "standard") ?? { key: "standard", title: t('common:standardAlerts'), data: [] },
        { key: "custom", title: t('common:customAlerts'), editable: true, data: fields },
    ];
    //#endregion
    if (isLoading) {
        return (
            <View
                style={styles.mainView}>
                <ActivityIndicator animating={true} size='large' color="#86BC25" />
            </View>
        )
    }

    return (
        <View style={styles.mainView}>
            <View style={common.titleButtonRow}>
                <CustomLabel
                    value={t('common:emplAlerts')}
                    type='MINORTITLE'
                    fontSize={20}
                />
                <View style={styles.row}>
                    <CustomButton
                        text={t("common:new")}
                        onPress={() => { handleAddRow(props.selectedEmployee.employeeID) }}
                        type="BUTTON_ADD"
                        textTop="0"
                        sz={15}
                    />
                    <CustomButton
                        onPress={handleSubmit(onSubmit)}
                        text={t('common:save')}
                        iconName='save-outline'
                        show='BUTTON_SAVE'
                        type='BUTTON_SAVE'
                        isGradient={true}
                        textTop={0}
                        sz={20}
                    />
                </View>
            </View>
            <SectionList
                sections={displaySections}
                initialNumToRender={30}
                keyExtractor={(item, index) => item.id ?? `${item.employeeID}-${index}`}
                contentContainerStyle={{ padding: 16 }}
                renderSectionHeader={({ section: { title } }) => (
                    <View style={[styles.sectionHeader]}>
                        <CustomLabel value={title} type="CARDTITLE" />
                    </View>
                )}
                renderItem={({ item, index, section }) => {
                    const [calendarVisible, setCalendarVisible] = useState(false);
                    const alertDate = useWatch({ control, name: `editable.${index}.alertDate` });

                    const isLast = index === section.data.length - 1;
                    let textColor = '';
                    let daysUntil = daysUntilCalculation(formatDateForUI(item.alertDate));
                    let labelText = '';
                    if (daysUntil > 0) {
                        labelText = `in ${daysUntil} days`;
                        specificColor = colors.background_approved
                        textColor = colors.text_approved
                    }
                    else if (daysUntil < 0) {
                        labelText = `Expired ${daysUntil} days ago`;
                        specificColor = colors.background_rejected
                        textColor = colors.text_pending
                    }
                    else if (daysUntil === 0) {
                        labelText = 'Expires Today';
                        specificColor = colors.card_pending
                        textColor = colors.text_pending
                    }

                    return (
                        section.key === 'custom' ?
                            <View style={[styles.item, isLast && styles.itemLast]}>
                                <View style={[common.cardstyle_transparent, common.titleButtonRow, { padding: 5, }]}>
                                    <CustomControlledDropdown
                                        control={control}
                                        name={`editable.${index}.monitorui`}
                                        type="EDIT_EMPLOYEE"
                                        options={alerts}
                                        setValue={setValue}
                                    />
                                    <CustomControlledInput
                                        name={`editable.${index}.comments`}
                                        control={control}
                                        type='MAININPUT'
                                    />
                                    <CustomInput
                                        value={formatDateForUI(alertDate?.startDate) || ''}
                                        editable={false}
                                        type="MAININPUT"
                                    />
                                    <Ionicons
                                        name={calendarVisible ? "calendar" : "calendar-outline"}
                                        style={styles.icons}
                                        onPress={() => setCalendarVisible(!calendarVisible)}
                                    />
                                    <Ionicons
                                        name={"trash-outline"}
                                        style={styles.icons}
                                        onPress={() => remove(index)}
                                    />
                                </View>
                                {calendarVisible && (
                                    <View style={styles.calendar}>
                                        <CustomControlledCalendar
                                            control={control}
                                            name={`editable.${index}.alertDate`}
                                            calendarType="RangePicker"
                                            allowSingleDate={true}
                                        />
                                    </View>
                                )}
                            </View>
                            :
                            <View style={[styles.item, isLast && styles.itemLast]}>
                                <View style={[common.cardstyle_transparent, common.titleButtonRow]}>
                                    <View style={[styles.row]}>
                                        <CustomLabel value={item.monitor} type="CARDTITLE" fontSize={16} />
                                    </View>
                                    <View style={styles.row}>
                                        <CustomLabel value={item?.alertDate ? formatDateForUI(item.alertDate) : 'Not Set'} type="CARDTITLE_MUTED" fontSize={16} />
                                        {item?.alertDate !== null &&
                                            <View style={styles.margin}>
                                                <CustomLabel
                                                    value={labelText}
                                                    type='STATUS'
                                                    specificColor={specificColor}
                                                    textColor={textColor} />
                                            </View>
                                        }
                                    </View>
                                </View>
                            </View>
                    )
                }
                }
                renderSectionFooter={() => <View style={{ height: 16 }} />}
            />
        </View>
    )

}
const makeStyles = (colors, width) =>
    StyleSheet.create({
        mainView: {
            flex: 1,
        },
        sectionHeader: {
            backgroundColor: colors.background_white,
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
        },
        item: {
            backgroundColor: colors.background_white,
            paddingHorizontal: 16,
            paddingVertical: 12,
        },
        itemLast: {
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
        },
        row: {
            flexDirection: 'row',
            marginHorizontal: 10,
        },
        margin: {
            marginVertical: 5,
        },
        icons: {
            fontSize: 24,
            marginHorizontal: 10,
        },
        calendar: {
            alignSelf: 'center',
            justifyContent: 'center'
        },
    })

export default HRAdminEmpAlerts;